import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import GLOBALS, { BOOLEAN_STRINGS } from 'client/constants/globals';
import { COOKIE_BANNER_DISMISSED } from 'client/constants/amplitudeTracker';
import { Tracking } from 'web-core/src/tracking';
import CookieUtils from 'web-core/src/utils/cookie';
import Translate from 'client/components/translate';
import 'client/styles/cookieBanner.scss';
import { initLD as initLDAction } from 'client/actions/launchDarkly';

const USER_PRIVACY_COOKIE_NAME = 'wasPrivacyBannerViewed';

type Props = {
  enableOsano: boolean;
  initLD: () => void;
  isPartnerSite: boolean;
  enableCookieBanner: boolean;
};

const mapStateToProps = (state) => {
  const partnershipType = _.get(
    state,
    'siteConfigs.partnerAgencyInfo.partnerInfo.partnershipType',
    '',
  );

  return {
    isPartnerSite: partnershipType === 'ENTERPRISE' || partnershipType === 'WHITE_LABEL',
    enableOsano: state.launchDarkly.flags['enable-osano'] || true,
    enableCookieBanner: state.launchDarkly.flags['enable-cookie-banner'] || false,
  };
};

const CookieBanner = ({ isPartnerSite, enableOsano, enableCookieBanner, initLD }: Props) => {
  const [wasPrivacyBannerViewed, setWasPrivacyBannerViewed] = useState<boolean>(true);

  const handleClick = useCallback(() => {
    setWasPrivacyBannerViewed(true);
    CookieUtils.setCookie(
      `${USER_PRIVACY_COOKIE_NAME}=${BOOLEAN_STRINGS.TRUE}`,
      GLOBALS.ONE_YEAR_IN_MILLISECONDS,
    );
    Tracking.track(COOKIE_BANNER_DISMISSED);
  }, []);

  const uiListener = useCallback(
    (component: string, action: string) => {
      if (component === 'dialog' && action === 'hide') {
        handleClick();
        window?.Osano?.cm.removeEventListener('osano-cm-ui-changed', uiListener);
      }
    },
    [handleClick],
  );

  useEffect(() => {
    initLD();

    const cookiePrivacyBannerViewed = CookieUtils.getCookie(USER_PRIVACY_COOKIE_NAME) === 'true';

    setWasPrivacyBannerViewed(cookiePrivacyBannerViewed);
  }, [initLD]);

  useEffect(() => {
    if (enableOsano && enableCookieBanner && !isPartnerSite && !wasPrivacyBannerViewed) {
      window?.Osano?.cm.addEventListener('osano-cm-initialized', () => {
        window?.Osano?.cm.showDialog();
        window?.Osano?.cm.addEventListener('osano-cm-ui-changed', uiListener);
      });
    }
  }, [enableOsano, isPartnerSite, enableCookieBanner, wasPrivacyBannerViewed, uiListener]);

  if (!enableCookieBanner || enableOsano || isPartnerSite || wasPrivacyBannerViewed) {
    return null;
  }

  return (
    <div className="cookie-banner-container">
      <div className="cookie-banner">
        <div>
          <Translate path="common.footer.cookieBanner" />
        </div>
        <button onClick={handleClick} onKeyDown={handleClick} className="title cookie-close">
          &times;
        </button>
      </div>
    </div>
  );
};

export default connect(mapStateToProps, { initLD: initLDAction })(CookieBanner);
